import { memo, useCallback, useEffect, useMemo } from "react";
import {
  Tldraw,
  DefaultStylePanel,
  type Editor,
  type TLPresenceUserInfo,
  type TLStore,
} from "tldraw";
import { useSync } from "@tldraw/sync";
import { CanvasStylePanel } from "@/canvas/components/CanvasPanels";
import { CANVAS_OPTIONS, CANVAS_UI_OVERRIDES } from "@/canvas/canvasOptions";
import {
  SPACES_SHAPE_UTILS,
  SPACES_COMPONENTS_BASE,
  noopAssets,
} from "../constants";
import { usePersonalSyncStore } from "../hooks/usePersonalSyncStore";
import {
  SHARED_CHANNEL,
  type SpaceSyncMultiplexer,
} from "../spaceSyncMultiplexer";
import { TLDRAW_LICENSE_KEY } from "@/constants/tldraw";
import { TldrawDrawToolRegion } from "@/drawlock/TldrawDrawToolRegion";
import { ET_CANVAS_ASSET_URLS } from "@ever-tutor/et-ui";

const spaceShapeUtils = SPACES_SHAPE_UTILS;

/**
 * Mounts/unmounts the personal sync subscription so a read→edit transition
 * always starts useSync from a clean slate. Without a keyed remount, useSync
 * captures `connect` once on first render and stays glued to whatever socket
 * it saw first (the disabled fallback when mode started as "read").
 *
 * Renders nothing — surfaces the store via onStoreChange so the parent can
 * wire it into useStudentStudentSharedCanvases.
 */
export const StudentPersonalSyncBridge = memo(
  function StudentPersonalSyncBridge({
    mux,
    userId,
    userInfo,
    onStoreChange,
  }: {
    mux: SpaceSyncMultiplexer;
    userId: string;
    userInfo: TLPresenceUserInfo | undefined;
    onStoreChange: (store: TLStore | null) => void;
  }) {
    const store = usePersonalSyncStore(mux, userId, userInfo);
    useEffect(() => {
      onStoreChange(store);
    }, [store, onStoreChange]);
    useEffect(
      () => () => {
        onStoreChange(null);
      },
      [onStoreChange],
    );
    return null;
  },
);

/**
 * Shared sync store backed by the multiplexer "shared" channel — this is the
 * student's primary canvas (slides + everyone's shared shapes). Personal-layer
 * records are mirrored INTO this canvas's editor.store by useStudentStudentSharedCanvases
 * so the student sees them, but the student's own writes route based on
 * collabMode (shared → here, local → personal store).
 */
export const StudentSharedCanvas = memo(function StudentSharedCanvas({
  mux,
  userInfo,
  onMount,
  stylePanel,
}: {
  mux: SpaceSyncMultiplexer;
  userInfo: TLPresenceUserInfo | undefined;
  onMount: (editor: Editor) => void;
  stylePanel: typeof DefaultStylePanel | typeof CanvasStylePanel;
}) {
  const connect = useCallback(() => mux.getChannel(SHARED_CHANNEL), [mux]);
  const store = useSync({
    connect,
    assets: noopAssets,
    userInfo,
    shapeUtils: spaceShapeUtils,
  });
  const components = useMemo(
    () => ({ ...SPACES_COMPONENTS_BASE, StylePanel: stylePanel }),
    [stylePanel],
  );

  return (
    <div className="tldraw-canvas">
      <Tldraw
        licenseKey={TLDRAW_LICENSE_KEY}
        assetUrls={ET_CANVAS_ASSET_URLS}
        store={store}
        shapeUtils={spaceShapeUtils}
        options={CANVAS_OPTIONS}
        overrides={CANVAS_UI_OVERRIDES}
        onMount={onMount}
        components={components}
      >
        <TldrawDrawToolRegion />
      </Tldraw>
    </div>
  );
});
